import { checkContainerNumber, type ContainerInput } from '@chukua/common';
import { Form, Input, Modal, Select } from 'antd';
import { useEffect } from 'react';
import type { ContainerSize, ContainerType } from '../../../../reference-data/reference-data.api';
import type { JobContainer } from '../../../jobs.api';

interface ContainerModalProps {
  open: boolean;
  /** The container being edited, or null when adding a new one. */
  container: JobContainer | null;
  containerSizes: ContainerSize[];
  containerTypes: ContainerType[];
  submitting: boolean;
  onSubmit: (input: ContainerInput) => Promise<boolean>;
  onCancel: () => void;
}

/** Add a container, or edit an existing one — same fields either way. Checks the ISO 6346 check digit before submitting, so a typo is caught here rather than round-tripping to the server. */
export function ContainerModal({ open, container, containerSizes, containerTypes, submitting, onSubmit, onCancel }: ContainerModalProps) {
  const [form] = Form.useForm<ContainerInput>();

  useEffect(() => {
    if (open)
      form.setFieldsValue({
        number: container?.number ?? '',
        sizeCode: container?.sizeCode,
        typeCode: container?.typeCode,
        seal: container?.seal ?? undefined,
      });
  }, [open, container, form]);

  async function submit() {
    await onSubmit(await form.validateFields());
  }

  return (
    <Modal
      open={open}
      title={container ? 'Edit container' : 'Add container'}
      onOk={submit}
      onCancel={onCancel}
      confirmLoading={submitting}
      destroyOnClose
      okText={container ? 'Save' : 'Add'}
    >
      <Form<ContainerInput> form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="number"
          label="Container number"
          normalize={(value: string) => value.toUpperCase()}
          rules={[
            { required: true, message: 'Enter a container number' },
            {
              validator: (_rule, value: string | undefined) => {
                if (!value) return Promise.resolve();
                const result = checkContainerNumber(value);
                return result.ok ? Promise.resolve() : Promise.reject(new Error(result.reason));
              },
            },
          ]}
        >
          <Input className="font-mono uppercase" autoFocus placeholder="CSQU3054383" />
        </Form.Item>
        <div className="grid grid-cols-2 gap-3">
          <Form.Item name="sizeCode" label="Size" rules={[{ required: true, message: 'Select a size' }]}>
            <Select options={containerSizes.map((s) => ({ value: s.code, label: s.name }))} />
          </Form.Item>
          <Form.Item name="typeCode" label="Type" rules={[{ required: true, message: 'Select a type' }]}>
            <Select options={containerTypes.map((t) => ({ value: t.code, label: t.name }))} />
          </Form.Item>
        </div>
        <Form.Item name="seal" label="Seal">
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
}
